import React, { useCallback, useMemo, useState } from "react";
import {
  DataTable,
  PricePositiveNagetive,
  ScriptPositionHistoryFilter,
} from "../Components";
import { useQuery } from "@tanstack/react-query";
import { API_POST_SCRIPTPOSITIONHISTORY } from "../utils/api/ApiConstant";
import { postFetchAxios } from "../Services/commonService";
import { stringToDate } from "../helper/common";

const ScriptPositionHistoryModal = ({ rowData }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [recordsPerPage, setRecordsPerPage] = useState(200);
  const [payloadData, setPayloadData] = useState({
    isSubmitFilter: null,
    server: null,
    fromDate: new Date(new Date().setHours(0, 0, 0, 0)),
    toDate: new Date(),
    scriptToken: null,
  });

  const {
    isLoading: sctiptPositionHistoryLoading,
    data: sctiptPositionHistoryData,
  } = useQuery({
    queryKey: ["sctiptPositionHistoryData", payloadData],
    queryFn: async () => {
      if (payloadData?.isSubmitFilter === null) return null;

      const payload = {
        server: payloadData?.server,
        fromDate: payloadData?.fromDate
          ? stringToDate(payloadData?.fromDate)
          : null,
        toDate: payloadData?.toDate ? stringToDate(payloadData?.toDate) : null,
        scriptToken: payloadData?.scriptToken,
      };

      const res = await postFetchAxios({
        url: API_POST_SCRIPTPOSITIONHISTORY,
        payload,
      });

      return res;
    },
  });

  const handleFilterChange = ({ filterData }) => {
    setPayloadData((prev) => ({
      isSubmitFilter: !prev.isSubmitFilter,
      ...filterData,
    }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRecordsperPage = (value) => {
    setRecordsPerPage(value);
  };

  const fetchData = useMemo(() => {
    if (!sctiptPositionHistoryData) return { data: [], pages: 0 };

    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;

    const filterData = sctiptPositionHistoryData
      .filter((item) => {
        const searchMatch =
          !searchTerm ||
          Object.values(item).some((val) =>
            String(val).toLowerCase().includes(searchTerm.toLowerCase())
          );

        return searchMatch;
      })
      .map((item, index) => ({
        ...item,
        _id: startIndex + index + 1, // Assuming unique id based on index
      }));

    return {
      data: filterData.slice(startIndex, endIndex),
      pages: Math.ceil(filterData.length / recordsPerPage),
    };
  }, [sctiptPositionHistoryData, currentPage, recordsPerPage, searchTerm]);

  const exportToCSV = useCallback(() => {
    if (!sctiptPositionHistoryData || sctiptPositionHistoryData.length === 0)
      return;

    const tableData = sctiptPositionHistoryData.map((item) => {
      return columns.reduce((acc, col) => {
        acc[col.header] = item[col.accessorKey];
        return acc;
      }, {});
    });

    const ws = XLSX.utils.json_to_sheet(tableData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TradeanAlytics");

    const csvFile = XLSX.write(wb, { bookType: "csv", type: "binary" });
    const blob = new Blob([csvFile], { type: "text/csv" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "script-position-history.csv";
    link.click();
  }, [sctiptPositionHistoryData]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "PositionDate",
        header: "Position Date",
        cell: ({ getValue }) => stringToDate(getValue()),
      },
      {
        accessorKey: "BuyQty",
        header: "Buy Qty",
        cell: ({ getValue }) => (
          <PricePositiveNagetive value={getValue()} isTextColor={false} />
        ),
      },
      {
        accessorKey: "SellQty",
        header: "Sell Qty",
        cell: ({ getValue }) => (
          <PricePositiveNagetive value={getValue()} isTextColor={false} />
        ),
      },
      {
        accessorKey: "OpenQuantity",
        header: "Open Quantity",
        cell: ({ getValue }) => (
          <PricePositiveNagetive value={getValue()} isTextColor={false} />
        ),
      },
    ],
    []
  );

  return (
    <>
      <ScriptPositionHistoryFilter
        rowData={rowData}
        handleFilterChange={handleFilterChange}
      />

      <DataTable
        columns={columns}
        data={fetchData.data}
        pages={fetchData.pages}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onRecordsPerPageChange={handleRecordsperPage}
        recordsPerPage={recordsPerPage}
        isLoading={sctiptPositionHistoryLoading}
        isSearchable={true}
        searchTerm={searchTerm}
        onSearchChange={(val) => setSearchTerm(val)}
        isPagination={true}
        isPageSize={true}
        isExportCsv={true}
        exportToCSV={exportToCSV}
        isIndexing={true}
      />
    </>
  );
};

export default ScriptPositionHistoryModal;
